import { AxiosResponse } from 'axios';
import moment from 'moment';
import { ChatHistoryData, ChatSessionResponse, ChatHistoryApiResponse, HttpClient } from '../../types/sidekick';
import getSidekickHttpClient from '../../common/sidekick-http';

// Create a single instance of the HTTP client to be reused across all methods
const httpClient: HttpClient = getSidekickHttpClient();

/**
 * Maps frontend field names to backend API field names for sorting
 * Only curatedResponses needs to be converted to singular form
 */
const mapSortFieldToApiField = (sortField: string): string => {
    // Only special case: frontend uses plural, backend expects singular
    return sortField === 'curatedResponses' ? 'curatedResponse' : sortField;
};

const getUserUtcOffset = (dateValue?: string): string => {
    return dateValue ? moment(dateValue).format('Z') : moment().format('Z');
};

const toDateTimeWithUserOffset = (dateValue: string, isStartOfDay: boolean): string => {
    if (!dateValue || dateValue.includes('T')) {
        return dateValue;
    }

    const selectedDate = moment(dateValue, 'YYYY-MM-DD', true);
    if (!selectedDate.isValid()) {
        return dateValue;
    }

    if (isStartOfDay) {
        return selectedDate.startOf('day').format('YYYY-MM-DDTHH:mm:ssZ');
    }

    const now = moment();
    const endDateTime = selectedDate.isSameOrAfter(now, 'day')
        ? now
        : selectedDate.endOf('day');

    return endDateTime.format('YYYY-MM-DDTHH:mm:ssZ');
};

/**
 * Get all chat history with pagination using POST request
 * @param page Page number
 * @param pageSize Number of items per page
 * @param sortBy Field to sort by
 * @param sortDirection Sort direction (asc/desc)
 * @param from Start date for filtering
 * @param to End date for filtering
 * @returns Promise with chat history data
 */
const getChatHistory = (
    page: number = 1,
    pageSize: number = 10,
    sortBy: string = 'created_at',
    sortDirection: string = 'desc',
    from?: string,
    to?: string,
    filters?: Record<string, string | number | string[]>
): Promise<AxiosResponse<ChatHistoryApiResponse>> => {
    const filterFrom = typeof filters?.from === 'string' ? filters.from : undefined;
    const filterTo = typeof filters?.to === 'string' ? filters.to : undefined;

    const normalizedFrom = toDateTimeWithUserOffset(from || filterFrom || '', true);
    const normalizedTo = toDateTimeWithUserOffset(to || filterTo || '', false);
    const { from: _ignoredFrom, to: _ignoredTo, ...remainingFilters } = filters || {};

    // Map the frontend sortBy field to the backend API field name
    const apiSortField = mapSortFieldToApiField(sortBy);

    // Prepare request body
    const requestBody = {
        page,
        pageSize,
        sortBy: apiSortField,
        sortDirection,
        ...(normalizedFrom && { from: normalizedFrom }),
        ...(normalizedTo && { to: normalizedTo }),
        ...remainingFilters
    };

    // Use the shared HTTP client instance with POST method
    return httpClient.post(`/api/v1/ChatHistory`, requestBody).catch((error: any) => {
        console.error(`Error fetching chat history:`, error);
        // Preserve the original error for better debugging
        throw error;
    });
};

/**
 * Maps the API response data to the format expected by the application
 * Includes null safety to handle missing or undefined fields
 * 
 * @param apiResponse The raw API response
 * @returns Mapped data in the format expected by the application
 */
export const mapChatHistoryResponse = (apiResponse: ChatHistoryApiResponse): { value: ChatHistoryData[], count: number } => {
    if (!apiResponse || !apiResponse.data) {
        console.error('Invalid API response received:', apiResponse);
        return { value: [], count: 0 };
    }

    // Map each item from the API response to the format expected by the application
    // Use nullish coalescing to provide safe defaults for all fields
    const mappedData: ChatHistoryData[] = apiResponse.data.map(item => ({
        emailId: item.userEmail ?? '',
        conversationId: item.chatId ?? '',
        conversationSubject: item.chatSubject ?? '',
        conversationCreatedAt: item.chatCreatedAt ?? '',
        conversationFeedback: item.chatFeedback,  // Already typed as string | null
        feedbackReason: item.feedbackReason ?? '',
        curatedResponses: item.curatedResponses ?? ''
    }));

    return {
        value: mappedData,
        count: apiResponse.totalCount ?? 0
    };
};

/**
 * Get chat session details by conversation ID
 * @param conversationId The conversation ID to retrieve details for
 * @param page Page number for pagination
 * @param pageSize Number of items per page
 * @returns Promise with chat session details
 */
const getChatSessionDetails = (
    conversationId: string,
    page: number = 1,
    pageSize: number = 10
): Promise<AxiosResponse<ChatSessionResponse>> => {
    // Validate input parameters
    if (!conversationId) {
        return Promise.reject(new Error('Conversation ID is required'));
    }

    // Build query parameters
    const params: Record<string, string | number> = {
        page,
        pageSize,
        sortBy: 'created_at',
        sortDirection: 'asc'
    };

    // Use the shared HTTP client instance
    return httpClient.get(`/api/v1/ChatHistory/session/${conversationId}/messages`, {
        params,
    }).catch((error: any) => {
        console.error(`Error fetching chat session details for conversation ${conversationId}:`, error);
        // Preserve the original error for better debugging
        throw error;
    });
};

/**
 * Get filter dataset by field and query
 * @param field The field to filter by
 * @param query The search query string
 * @returns Promise with filtered dataset results
 */
interface FilterDataSetResponse extends Array<string> { }

const getFilterDataSet = (
    field: string,
    query: string,
    limit: number = 200,
    signal?: AbortSignal
): Promise<AxiosResponse<FilterDataSetResponse>> => {
    if (!field || !query) {
        return Promise.reject(new Error('Both field and query parameters are required'));
    }

    return httpClient.get(`/api/v1/ChatHistory/search`, {
        params: { field, query, limit },
        signal
    }).catch((error: any) => {
        if (error?.name !== 'AbortError' && error?.name !== 'CanceledError') {
            console.error(`Error fetching filter dataset:`, error);
        }
        throw error;
    });
};

/**
 * Export chat history data in the specified format
 * @param format The format to export (csv or excel)
 * @param filters Optional filters to apply to the export
 * @returns Promise with the exported data
 */
const exportChatHistory = (
    format: 'csv' | 'excel',
    filters?: Record<string, string>
): Promise<AxiosResponse<Blob>> => {
    const params: Record<string, string> = {
        format,
        ...filters || {}
    };

    // Use the shared HTTP client instance
    return httpClient.get(`/api/ChatHistory/export`, {
        params,
        responseType: 'blob'
    }).catch((error: any) => {
        console.error(`Error exporting chat history:`, error);
        throw error;
    });
};

const sidekickService = {
    getChatHistory,
    getChatSessionDetails,
    getFilterDataSet,
    exportChatHistory
};

export default sidekickService;